import type {Ref} from 'react';
import {forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState} from 'react';

import {GoogleMapsContext, useMapsLibrary} from '@vis.gl/react-google-maps';

type PolylineEventProps = {
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onDrag?: (e: google.maps.MapMouseEvent) => void;
  onDragStart?: (e: google.maps.MapMouseEvent) => void;
  onDragEnd?: (e: google.maps.MapMouseEvent) => void;
  onMouseOver?: (e: google.maps.MapMouseEvent) => void;
  onMouseOut?: (e: google.maps.MapMouseEvent) => void;
};

type PolylineCustomProps = {
  /**
   * this is an encoded string for the path, will be decoded and used as a path
   */
  encodedPath?: string;
};

export type PolylineProps = google.maps.PolylineOptions &
  PolylineEventProps &
  PolylineCustomProps;

export type PolylineRef = Ref<google.maps.Polyline | null>;

function usePolyline(props: PolylineProps) {
  const {
    onClick,
    onDrag,
    onDragStart,
    onDragEnd,
    onMouseOver,
    onMouseOut,
    encodedPath,
    ...polylineOptions
  } = props;

  // Latest callbacks ref: aggiornato in useEffect (non in render) per
  // evitare violazione react-hooks/refs. I listener registrati piu'
  // sotto leggono callbacksRef.current al fire dell'evento, quindi
  // hanno sempre la versione piu' recente delle callback senza dover
  // ri-attaccarsi ad ogni cambio.
  const callbacksRef = useRef<Record<string, ((e: google.maps.MapMouseEvent) => void) | undefined>>({});
  useEffect(() => {
    callbacksRef.current = {
      onClick,
      onDrag,
      onDragStart,
      onDragEnd,
      onMouseOver,
      onMouseOut,
    };
  });

  const geometryLibrary = useMapsLibrary('geometry');

  // Istanza Polyline creata una sola volta a mount via lazy state init.
  // Il vecchio pattern `useRef(new google.maps.Polyline()).current`
  // creava un'istanza nuova ad ogni render (function arg, non lazy
  // init) e leakkava memoria. Inoltre la regola react-hooks/refs lo
  // segnalava come accesso a ref in render.
  const [polyline] = useState(() => new google.maps.Polyline());

  // setOptions ad ogni render (le opzioni cambiano spesso e la libreria
  // gestisce internamente l'optimizzazione).
  useEffect(() => {
    polyline.setOptions(polylineOptions);
  });

  // Decodifica path quando arriva encodedPath o geometryLibrary.
  useEffect(() => {
    if (!encodedPath || !geometryLibrary) return;
    const path = geometryLibrary.encoding.decodePath(encodedPath);
    polyline.setPath(path);
  }, [polyline, encodedPath, geometryLibrary]);

  const map = useContext(GoogleMapsContext)?.map;

  // Attach al mappa (e cleanup all'unmount o cambio mappa).
  useEffect(() => {
    if (!map) {
      if (map === undefined)
        console.error('<Polyline> has to be inside a Map component.');

      return;
    }

    polyline.setMap(map);

    return () => {
      polyline.setMap(null);
    };
  }, [map, polyline]);

  // Event listeners agganciati una volta sola al polyline (le callback
  // vengono lette via callbacksRef.current, sempre fresche).
  useEffect(() => {
    const gme = google.maps.event;
    const listeners: google.maps.MapsEventListener[] = [];
    const events: ReadonlyArray<readonly [string, keyof PolylineEventProps]> = [
      ['click', 'onClick'],
      ['drag', 'onDrag'],
      ['dragstart', 'onDragStart'],
      ['dragend', 'onDragEnd'],
      ['mouseover', 'onMouseOver'],
      ['mouseout', 'onMouseOut'],
    ];
    events.forEach(([eventName, cbKey]) => {
      const listener = gme.addListener(polyline, eventName, (e: google.maps.MapMouseEvent) => {
        const cb = callbacksRef.current[cbKey];
        if (cb) cb(e);
      });
      listeners.push(listener);
    });

    return () => {
      listeners.forEach((l) => l.remove());
    };
  }, [polyline]);

  return polyline;
}

/**
 * Component to render a polyline on a map
 */
// eslint-disable-next-line react/display-name
export const Polyline = forwardRef((props: PolylineProps, ref: PolylineRef) => {
  const polyline = usePolyline(props);

  useImperativeHandle(ref, () => polyline, [polyline]);

  return null;
});
